import { ChartContainer } from '@components/chart';
import { DownloadSVG, UserMic2 } from '@components/icon/svg';
import CustomToggle from '@components/shared/CustomToggle';
import { BarChart, PieChart } from '@mui/icons-material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
// eslint-disable-next-line no-unused-vars
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
// eslint-disable-next-line no-unused-vars
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
// eslint-disable-next-line no-unused-vars
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
// eslint-disable-next-line no-unused-vars
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InterpreterModeOutlinedIcon from '@mui/icons-material/InterpreterModeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha, Box, Card, Grid, Popover, Tooltip, Typography, useTheme } from '@mui/material';
import { handleSaveImage } from '@utils/saveChartImage';
import ReactECharts from 'echarts-for-react';
import _ from 'lodash';
import { useRef, useState } from 'react';

const colorList = [
  '#C9B7FF', // Lavender
  '#9BC5FF', // Baby Blue
  '#66C0FF', // Sky Blue
  '#6EE7B7', // Mint Green
  '#F9D17B', // Pastel Yellow
  '#FCA5A5', // Soft Coral
  '#FDB4FF', // Light Pink
  '#FFB366', // Peach Orange
  '#7DD3FC', // Light Cyan Blue
  '#A78BFA', // Medium Purple
  '#FFD6A5', // Apricot
  '#B5E48C', // Lime Green
  '#FFADAD', // Warm Pink
  '#9BF6FF', // Aqua
  '#BDB2FF', // Periwinkle
  '#C8FFD4', // Mint Cream
  '#FFCBC1', // Peach Blush
  '#FFF2B2', // Soft Lemon
  '#D7C0F9', // Pale Violet
  '#A0E7E5', // Turquoise Pastel
];

/**
 * Consolidated summary panel showing total responses, a segmented status bar,
 * and individual status counts in one card.
 */
function StatBox({ icon: Icon, iconColor, label, value, tooltip }) {
  const theme = useTheme();
  return (
    <Tooltip title={tooltip || label} arrow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.grey[100], 0.6),
          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          minWidth: 100,
        }}
      >
        <Icon sx={{ fontSize: 20, color: iconColor || 'text.secondary' }} />
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {label}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

function InterviewSummaryPanel({ completionStatistic }) {
  const theme = useTheme();
  const [derivedAnchor, setDerivedAnchor] = useState(null);

  // Primary interview status counts
  const completed = completionStatistic?.total_completed_human || 0;
  const incomplete = completionStatistic?.total_incomplete || 0;
  const active = completionStatistic?.total_active || 0;
  const draft = completionStatistic?.total_draft || 0;

  // Type counts
  const uploaded = completionStatistic?.total_uploaded || 0;
  const derived = completionStatistic?.total_derived || 0;
  const aiParticipant = completionStatistic?.total_ai_participant || 0;

  const humanInterviewsTotal = completed + incomplete + active + draft;
  // Derived is shown separately on demand — NOT included in total
  const total = humanInterviewsTotal + uploaded + aiParticipant;

  // Count how many type categories are non-zero
  const nonZeroTypes = [
    { count: humanInterviewsTotal, type: 'human' },
    { count: uploaded, type: 'uploaded' },
    { count: aiParticipant, type: 'ai' },
  ].filter((t) => t.count > 0);

  // If only one type exists, the breakdown is redundant — hide the type boxes
  const showBreakdown = nonZeroTypes.length > 1;

  // Dynamic title based on the only non-zero type
  const totalLabel = (() => {
    if (nonZeroTypes.length !== 1) return 'Total Responses';
    const onlyType = nonZeroTypes[0].type;
    if (onlyType === 'human') return 'Total Human Interviews';
    if (onlyType === 'uploaded') return 'Total Uploaded Transcripts';
    if (onlyType === 'ai') return 'Total AI Participant Responses';
    return 'Total Responses';
  })();

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        boxShadow: `0 1px 3px ${alpha(theme.palette.grey[500], 0.08)}`,
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Pill-style section title */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            mb: 2.5,
            borderRadius: 999,
            background: 'linear-gradient(135deg, #7d5ae8 0%, #9b7ff5 100%)',
            boxShadow: `0 4px 12px ${alpha('#7d5ae8', 0.3)}`,
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ color: 'white', letterSpacing: 0.5 }}>
            Responses Overview
          </Typography>
        </Box>

        {/* Header row: total + (optional) derived chip */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500, letterSpacing: 0.3 }}
            >
              {totalLabel}
            </Typography>
            <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.25 }}>
              {total}
            </Typography>
          </Box>
          {derived > 0 && (
            <>
              <Box
                onClick={(e) => setDerivedAnchor(e.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: alpha('#1565c0', 0.08),
                  border: `1px solid ${alpha('#1565c0', 0.2)}`,
                  cursor: 'pointer',
                  alignSelf: 'flex-end',
                  mb: 0.5,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: alpha('#1565c0', 0.15),
                  },
                }}
              >
                <EditNoteOutlinedIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                <Typography variant="caption" fontWeight={600} sx={{ color: '#1565c0' }}>
                  +{derived} derived
                </Typography>
              </Box>
              <Popover
                open={Boolean(derivedAnchor)}
                anchorEl={derivedAnchor}
                onClose={() => setDerivedAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box sx={{ p: 2, maxWidth: 280 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {derived} derived transcript{derived > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Created from transcript workflows like translation, PII redaction, or
                    enhancement. These are not counted in the total responses above.
                  </Typography>
                </Box>
              </Popover>
            </>
          )}
        </Box>

        {/* Type breakdown — only show when there are multiple types */}
        {showBreakdown && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2.5 }}>
            {humanInterviewsTotal > 0 && (
              <StatBox
                icon={InterpreterModeOutlinedIcon}
                iconColor="#5e35b1"
                label="Human Interviews"
                value={humanInterviewsTotal}
                tooltip="AI-moderated interviews with human participants"
              />
            )}
            {uploaded > 0 && (
              <StatBox
                icon={UploadFileOutlinedIcon}
                iconColor="#e65100"
                label="Uploaded transcripts"
                value={uploaded}
                tooltip="Manually uploaded audio, video, or text transcripts"
              />
            )}
            {aiParticipant > 0 && (
              <StatBox
                icon={SmartToyOutlinedIcon}
                iconColor="#00897b"
                label="AI Participant"
                value={aiParticipant}
                tooltip="Interviews with AI-generated participants"
              />
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
}

function HumanInterviewsOverview({ completionStatistic, children }) {
  const theme = useTheme();
  const completed = completionStatistic?.total_completed_human || 0;
  const incomplete = completionStatistic?.total_incomplete || 0;
  const active = completionStatistic?.total_active || 0;
  const draft = completionStatistic?.total_draft || 0;
  const humanInterviewsTotal = completed + incomplete + active + draft;

  const statusSegments = [
    { label: 'Complete', value: completed, color: '#4caf50' },
    { label: 'Incomplete', value: incomplete, color: '#ff9800' },
    { label: 'Active', value: active, color: '#2196f3' },
    { label: 'Draft', value: draft, color: '#9e9e9e' },
  ].filter((s) => s.value > 0);

  if (humanInterviewsTotal === 0) return children;

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>
          Human Interviews Overview
        </Typography>
        <Tooltip
          title="Below charts and stats are from AI moderator interviews with human participants"
          arrow
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        </Tooltip>
      </Box>

      {/* Status bar + breakdown */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: 8,
            borderRadius: 4,
            overflow: 'hidden',
            mb: 1.5,
            bgcolor: alpha(theme.palette.grey[300], 0.3),
          }}
        >
          {statusSegments.map((seg) => (
            <Tooltip key={seg.label} title={`${seg.label}: ${seg.value}`}>
              <Box
                sx={{
                  width: `${(seg.value / humanInterviewsTotal) * 100}%`,
                  bgcolor: seg.color,
                  transition: 'width 0.4s ease',
                  minWidth: seg.value > 0 ? 4 : 0,
                }}
              />
            </Tooltip>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
          {statusSegments.map((seg) => (
            <Box key={seg.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: seg.color, flexShrink: 0 }}
              />
              <Typography variant="caption" color="text.secondary">
                {seg.label}
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {seg.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {children}
    </Box>
  );
}

export default function ResponseInsight({
  completionStatistic,
  deviceUsages,
  surveyParticipant,
  timeToComplete,
  voiceFeatureUsages,
  isPrimary,
  hideVoiceFeatureUsages,
  canEdit,
}) {
  return (
    <Box p={{ xs: 2, md: 2, lg: 4 }}>
      {/* Response Summary Panel */}
      <InterviewSummaryPanel completionStatistic={completionStatistic} />

      {/* Human Interviews Overview — title, status bar, and charts */}
      <HumanInterviewsOverview completionStatistic={completionStatistic}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <SurveyTimeToComplete
              timeToComplete={timeToComplete}
              canAddToReport={canEdit}
              hasData={timeToComplete?.length > 0}
              isPrimary={isPrimary}
              controller={<DownloadSVG />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <SurveyGeographyMap
              canAddToReport={canEdit}
              surveyMap={surveyParticipant ?? []}
              hasData={surveyParticipant?.length > 0}
              isPrimary={isPrimary}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <SurveyDeviceUsages
              canAddToReport={canEdit}
              deviceUsages={deviceUsages || []}
              hasData={deviceUsages?.length > 0}
              isPrimary={isPrimary}
            />
          </Grid>
          {!hideVoiceFeatureUsages && (
            <Grid item xs={12} md={6} lg={4}>
              <SurveyVoiceOptionUsage
                voiceFeatureUsages={voiceFeatureUsages || []}
                isPrimary={isPrimary}
                hasData={voiceFeatureUsages?.length > 0}
              />
            </Grid>
          )}
        </Grid>
      </HumanInterviewsOverview>
    </Box>
  );
}

function binData(data) {
  const bins = {
    '0-15': 0,
    '15-30': 0,
    '30-45': 0,
    '45-60': 0,
    '60+': 0,
  };

  data.forEach((item) => {
    const time = parseFloat(item);

    if (time >= 0 && time < 15) {
      bins['0-15'] += 1;
    } else if (time >= 15 && time < 30) {
      bins['15-30'] += 1;
    } else if (time >= 30 && time < 45) {
      bins['30-45'] += 1;
    } else if (time >= 45 && time < 60) {
      bins['45-60'] += 1;
    } else {
      bins['60+'] += 1;
    }
  });

  return Object.values(bins);
}

const SurveyTimeToComplete = ({ timeToComplete, hasData, isPrimary, canAddToReport }) => {
  const [chartType, setChartType] = useState('bar');
  const chartRef = useRef(null);
  const histogramData = binData(timeToComplete);

  const option = {
    grid: { top: 8, right: 8, bottom: 54, left: 55 },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: {
      type: 'category',
      data: ['0-15', '15-30', '30-45', '45-60', '60+'], // Categories from the data
      name: 'Time Range (minutes)', // Axis label
      nameLocation: 'middle',
      nameGap: 30,
    },
    yAxis: {
      type: 'value',
      name: 'Number of Respondents', // Axis label
      nameLocation: 'middle',
      nameGap: 40,
    },
    series: [
      {
        name: 'Count',
        type: 'bar',
        data: histogramData, // Values corresponding to each category
        itemStyle: {
          color: '#C9B7FF',
        },
      },
    ],
  };
  const pieOption = {
    grid: { top: 8, right: 8, bottom: 54, left: 55 },
    tooltip: {
      trigger: 'item',
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [
      {
        name: 'Count',
        type: 'pie',
        radius: '80%',
        itemStyle: {
          normal: {
            color: (params) => {
              const index = params.dataIndex;
              return colorList[index % colorList.length];
            },

            textStyle: {
              fontSize: 14,
              color: (params) => {
                const index = params.dataIndex;
                return colorList[index % colorList.length];
              },
            },
          },
        },
        data: histogramData.map((count, index) => {
          const name = index === 4 ? '60+' : `${index * 15}-${(index + 1) * 15}`;
          return { name, value: count, label: { show: count > 0 } };
        }),
      },
    ],
  };

  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Completion Time (min)'}
      chartRef={chartRef}
      icon={<AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />}
      handleSaveImage={() => handleSaveImage({ chartRef, fileName: 'Study Completion Time' })}
      controller={
        <>
          <CustomToggle
            options={[
              { value: 'bar', icon: BarChart, label: 'Bar Chart view' },
              { value: 'pie', icon: PieChart, label: 'Pie chart view' },
            ]}
            value={chartType}
            onChange={(value) => setChartType(value)}
          />
        </>
      }
    >
      {hasData ? (
        <ReactECharts
          ref={chartRef}
          option={chartType === 'bar' ? option : pieOption}
          style={{ height: '100%', width: '100%' }}
          notMerge={true} // Ensures old options are completely replaced
          lazyUpdate={false}
        />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const SurveyGeographyMap = ({ surveyMap, hasData, isPrimary, canAddToReport }) => {
  const [chart, setChart] = useState('pie');
  const chartRef = useRef();
  const barOptions = {
    grid: { top: 8, right: 8, bottom: 24, left: 55 },
    xAxis: {
      type: 'category',
      axisLabel: {
        inside: true,
        color: '#000',
        rotate: 90,
      },
      z: 10,
      data: _.map(surveyMap, 'country'),
    },
    yAxis: {
      type: 'value',
      name: 'Number of Respondents',
      nameLocation: 'middle',
      nameGap: 40,
    },
    dataZoom: [
      {
        type: 'inside',
        startValue: 0,
        endValue: 100,
      },
    ],
    series: [
      {
        data: _.map(surveyMap, 'participation_count'),
        type: 'bar',
        itemStyle: {
          color: '#C9B7FF',
        },
        smooth: true,
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
  };

  const pieOptions = {
    tooltip: {
      trigger: 'item',
    },

    series: [
      {
        name: 'Country',
        type: 'pie',
        radius: '80%',
        itemStyle: {
          normal: {
            color: (params) => {
              const index = params.dataIndex;
              return colorList[index % colorList.length];
            },

            textStyle: {
              fontSize: 14,
              color: (params) => {
                const index = params.dataIndex;
                return colorList[index % colorList.length];
              },
            },
          },
        },
        data: surveyMap.map((item) => ({
          name: item.country,
          value: item.participation_count,
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };

  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Demographics by Country'}
      icon={<OutlinedFlagIcon sx={{ fontSize: 18 }} />}
      chartRef={chartRef}
      handleSaveImage={() =>
        handleSaveImage({ chartRef, fileName: 'Response Demographics by Country' })
      }
      controller={
        <>
          <CustomToggle
            options={[
              { value: 'bar', icon: BarChart, label: 'Bar Chart view' },
              { value: 'pie', icon: PieChart, label: 'Pie chart view' },
            ]}
            value={chart}
            onChange={(value) => setChart(value)}
          />
        </>
      }
    >
      {hasData ? (
        <ReactECharts
          ref={chartRef}
          option={chart === 'bar' ? barOptions : pieOptions}
          style={{ height: '100%' }}
          notMerge={true} // Ensures old options are completely replaced
          lazyUpdate={false}
        />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const SurveyDeviceUsages = ({ deviceUsages, hasData, isPrimary, canAddToReport }) => {
  const [chart, setChart] = useState('bar');

  const chartRef = useRef();
  const sortedDeviceUsages = _.sortBy(deviceUsages, 'usage_count');
  const options = {
    grid: { top: 8, right: 30, bottom: 24, left: 100 },
    color: '#7d5ae8',
    xAxis: {
      type: 'value',
    },
    yAxis: {
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#999',
      },
      type: 'category',
      data: _.map(sortedDeviceUsages, 'device_name'),
    },

    series: [
      {
        data: _.map(sortedDeviceUsages, 'usage_count'),
        type: 'bar',
        itemStyle: {
          color: '#C9B7FF',
        },
        label: {
          show: true,
          position: 'right',
        },
        smooth: true,
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
  };

  const pieOptions = {
    grid: { top: 8, right: 30, bottom: 24, left: 100 },
    color: ['#7d5ae8'],
    tooltip: {
      trigger: 'item',
    },
    series: [
      {
        data: _.map(sortedDeviceUsages, (item) => ({
          value: item.usage_count,
          name: item.device_name,
        })),
        itemStyle: {
          normal: {
            color: (params) => {
              const index = params.dataIndex;
              return colorList[index % colorList.length];
            },

            textStyle: {
              fontSize: 14,
              color: (params) => {
                const index = params.dataIndex;
                return colorList[index % colorList.length];
              },
            },
          },
        },
        type: 'pie',
        radius: '80%',
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}: {c} ({d}%)',
        },
        smooth: true,
      },
    ],
  };

  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Device Usage'}
      icon={<DesktopMacOutlinedIcon sx={{ fontSize: 18 }} />}
      chartRef={chartRef}
      handleSaveImage={() => handleSaveImage({ chartRef, fileName: 'Device Usages' })}
      controller={
        <>
          <CustomToggle
            options={[
              { value: 'bar', icon: BarChart, label: 'Bar Chart view' },
              { value: 'pie', icon: PieChart, label: 'Pie chart view' },
            ]}
            value={chart}
            onChange={(value) => setChart(value)}
          />
        </>
      }
    >
      {hasData ? (
        <ReactECharts
          ref={chartRef}
          option={chart === 'bar' ? options : pieOptions}
          style={{ height: '100%' }}
          notMerge={true}
          lazyUpdate={false}
        />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const SurveyVoiceOptionUsage = ({ voiceFeatureUsages, hasData, isPrimary, canAddToReport }) => {
  const chartRef = useRef();
  const sortData = _.sortBy(voiceFeatureUsages, 'usage_frequency');
  const data = _.map(sortData, 'count');
  const category = _.map(sortData, 'usage_frequency');
  const options = {
    grid: { top: 8, right: 8, bottom: 54, left: 55 },
    xAxis: {
      type: 'category',
      name: 'Usage frequency',
      nameLocation: 'middle',
      nameGap: 40,
      data: category,
    },
    yAxis: {
      type: 'value',
      name: 'Number of Respondents',
      nameLocation: 'middle',
      nameGap: 40,
    },
    series: [
      {
        name: 'Users',
        data: data || [],
        type: 'bar',
        itemStyle: {
          color: '#C9B7FF',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
  };
  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Voice Feature Usage'}
      icon={<UserMic2 style={{ width: 18, height: 18 }} />}
      chartRef={chartRef}
      handleSaveImage={() => handleSaveImage({ chartRef, fileName: 'Voice Feature Usages' })}
    >
      {hasData ? (
        <ReactECharts ref={chartRef} option={options} style={{ height: '100%' }} />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const NoDataAvailable = ({ message }) => {
  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <Typography variant="subtitle2">{message}</Typography>
    </Box>
  );
};
